import React, { useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  InputNumber,
  DatePicker,
  message,
} from 'antd';
import { SendIcon } from 'lucide-react';
import dayjs from 'dayjs';
import EmployeeFundService, {
  FundTransferDto,
} from '../../service/employee-service/Employee-fund.service';

interface TransferFormProps {
  employeeId: number;
  currentBalance: number;
  onTransferSuccess: () => void;
}

const TransferForm: React.FC<TransferFormProps> = ({
  employeeId,
  currentBalance,
  onTransferSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);

      const transferDto: FundTransferDto = {
        amount: values.amount,
        description: values.description || 'Chuyển tiền về tài khoản công ty',
        transferDate: values.transferDate
          ? values.transferDate.toISOString()
          : undefined,
      };

      await EmployeeFundService.transferToCompanyAccount(
        employeeId,
        transferDto
      );

      message.success('Chuyển tiền thành công!');
      form.resetFields();
      onTransferSuccess();
    } catch (error: any) {
      console.error('Transfer error:', error);
      message.error(error.response?.data || 'Có lỗi xảy ra khi chuyển tiền');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      title={
        <div className="flex items-center gap-2">
          <SendIcon className="h-5 w-5" />
          <span>Chuyển tiền về công ty</span>
        </div>
      }
      className="mb-6"
    >
      <div className="mb-4 p-3 bg-blue-50 rounded-lg">
        <p className="text-sm text-gray-600">Số dư hiện tại:</p>
        <p className="text-lg font-semibold text-blue-600">
          {currentBalance.toLocaleString('vi-VN')} đ
        </p>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        <Form.Item
          label="Số tiền chuyển"
          name="amount"
          rules={[
            { required: true, message: 'Vui lòng nhập số tiền' },
            {
              type: 'number',
              min: 1,
              message: 'Số tiền phải lớn hơn 0',
            },
            {
              validator: (_, value) => {
                if (value && value > currentBalance) {
                  return Promise.reject(
                    'Số tiền chuyển không được vượt quá số dư hiện tại'
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <InputNumber
            style={{ width: '100%' }}
            placeholder="Nhập số tiền muốn chuyển"
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            }
            parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
            addonAfter="đ"
          />
        </Form.Item>

        <Form.Item label="Mô tả" name="description">
          <Input.TextArea
            placeholder="Mô tả lý do chuyển tiền (tùy chọn)"
            rows={3}
          />
        </Form.Item>

        <Form.Item label="Ngày chuyển" name="transferDate">
          <DatePicker
            style={{ width: '100%' }}
            placeholder="Chọn ngày chuyển (mặc định là hôm nay)"
            format="DD/MM/YYYY"
            defaultValue={dayjs()}
          />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            icon={<SendIcon className="h-4 w-4" />}
            size="large"
            className="w-full"
          >
            {loading ? 'Đang chuyển...' : 'Chuyển tiền'}
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default TransferForm;
